/** RaiderMapCR - Admin room editor tab with map and room editing */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  TextField,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useMediaQuery,
  Slide
} from '@mui/material';
import { 
  People as PeopleIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import useFloorPlan from '../hooks/useFloorPlan';

const AdminRoomsTab = () => {
  const [currentFloor, setCurrentFloor] = useState('F1');
  const [viewMode, setViewMode] = useState('occupancy');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [editedRooms, setEditedRooms] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [hoveredRoomId, setHoveredRoomId] = useState(null);
  const [roomPopupVisible, setRoomPopupVisible] = useState(false);
  const [showPreviousRoom, setShowPreviousRoom] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const svgRef = useRef(null);
  const popupRef = useRef(null);
  const transitionTimeoutRef = useRef(null);
  const cleanupTimeoutRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const {
    svgContent,
    rooms,
    isLoaded,
    error,
    applySvgColors,
    applyLabelColors,
    makePathsVisible,
    highlightRoom,
    unhighlightRoom
  } = useFloorPlan(currentFloor);

  const hasValidStatus = (room) => {
    return room && room.status && room.status !== 'unknown' && room.status.trim() !== '';
  };

  const hasValidCapacity = (room) => {
    return room && room.capacity && room.capacity > 0;
  };

  const handleFloorChange = (event, newFloor) => {
    if (newFloor && newFloor !== currentFloor) {
      setCurrentFloor(newFloor);
      setSelectedRoom(null);
      setEditorOpen(false);
      setRoomPopupVisible(false);
      setHoveredRoomId(null);
    }
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const manageSvgLayering = useCallback((svgElement, hoveredRoomId = null, selectedRoomId = null) => {
    if (!svgElement || !rooms.length) return;
    const hasHighlightedRoom = svgElement.querySelector('.room-path.highlighted');
    if (hasHighlightedRoom) return;
    // CRITICAL: Only work with elements that exist in current SVG (prevents floor-switch bugs)
    const currentSvgPathIds = Array.from(svgElement.querySelectorAll('path')).map(p => p.id).filter(id => id);
    const validRooms = rooms.filter(room => currentSvgPathIds.includes(room.id));
    if (validRooms.length === 0) return;
    const currentOrder = Array.from(svgElement.querySelectorAll('path')).map(p => p.id);
    const interactiveRooms = validRooms.filter(r => r.type !== "structure").map(r => r.id);
    const structureRooms = validRooms.filter(r => r.type === "structure").map(r => r.id);
    const interactiveRoomsInOrder = interactiveRooms.every(roomId => {
      const roomIndex = currentOrder.indexOf(roomId);
      const structureIndexes = structureRooms.map(sId => currentOrder.indexOf(sId)).filter(i => i >= 0);
      return roomIndex >= 0 && structureIndexes.every(sIndex => sIndex < roomIndex);
    });
    if (interactiveRoomsInOrder && !selectedRoomId && !hoveredRoomId) {
      return;
    }
    const iconsOverlay = svgElement.querySelector('#icons-overlay');
    const labelsOverlay = svgElement.querySelector('#labels-overlay');
    const roomElements = validRooms.map(room => {
      const pathElement = svgElement.querySelector(`#${room.id}`);
      if (pathElement) {
        return { element: pathElement, room };
      }
      return null;
    }).filter(Boolean);
    roomElements.sort((a, b) => {
      if (a.room.id === selectedRoomId) return 1;
      if (b.room.id === selectedRoomId) return -1;
      if (a.room.id === hoveredRoomId && a.room.id !== selectedRoomId) return 1;
      if (b.room.id === hoveredRoomId && b.room.id !== selectedRoomId) return -1;
      if (a.room.type !== "structure" && b.room.type === "structure") return 1;
      if (a.room.type === "structure" && b.room.type !== "structure") return -1;
      if (a.room.type !== "structure" && b.room.type !== "structure") return 0;
      if (a.room.type === "structure" && b.room.type === "structure") return 0;
      
      return 0;
    });
    roomElements.forEach(({ element }) => {
      const firstOverlay = iconsOverlay || labelsOverlay;
      if (firstOverlay) {
        svgElement.insertBefore(element, firstOverlay);
      } else {
        svgElement.appendChild(element);
      }
    });
    if (iconsOverlay && labelsOverlay) {
      svgElement.appendChild(iconsOverlay);
      svgElement.appendChild(labelsOverlay);
    } else if (labelsOverlay) {
      svgElement.appendChild(labelsOverlay);
    } else if (iconsOverlay) {
      svgElement.appendChild(iconsOverlay);
    }
  }, [rooms]);

  const openEditorForRoom = (room) => {
    setSelectedRoom(room);
    
    setFormData({
      name: room.name || '',
      type: room.type || 'other',
      status: room.status || 'unknown',
      capacity: room.capacity ?? 0,
      alias: room.alias || '',
      default_status: room.default_status || 'available'
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setSelectedRoom(null);
    setRoomPopupVisible(false);
    if (selectedRoom) {
      const svgElement = svgRef.current?.querySelector('svg');
      if (svgElement) {
        unhighlightRoom(selectedRoom.id, svgElement, viewMode);
        setHoveredRoomId(null);
        manageSvgLayering(svgElement, null, null);
      }
    }
  };

  const handleRoomClick = useCallback((roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      if (selectedRoom && selectedRoom.id === roomId) {
        return;
      }

      const svgElement = svgRef.current?.querySelector('svg');
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      setShowPreviousRoom(false);
      setIsTransitioning(false);

      if (selectedRoom) {
        setIsTransitioning(true);
        if (svgElement) {
          unhighlightRoom(selectedRoom.id, svgElement, viewMode);
        }
        setShowPreviousRoom(true);
        setRoomPopupVisible(false);
        transitionTimeoutRef.current = setTimeout(() => {
          setShowPreviousRoom(false);
          setSelectedRoom(room);
          setHoveredRoomId(null);
          manageSvgLayering(svgElement, null, room.id);
          if (svgElement) {
            highlightRoom(room.id, svgElement, viewMode);
          }
          setRoomPopupVisible(true);
          setIsTransitioning(false);
          
          transitionTimeoutRef.current = null;
        }, 200);
      } else {
        setSelectedRoom(room);
        setHoveredRoomId(null);
        manageSvgLayering(svgElement, null, room.id);
        if (svgElement) {
          highlightRoom(room.id, svgElement, viewMode);
        }
        
        setRoomPopupVisible(true);
      }
    }
  }, [rooms, selectedRoom, highlightRoom, unhighlightRoom, viewMode, manageSvgLayering]);

  const handleSvgClick = useCallback((event) => {
    const clickedElement = event.target;
    const tagName = clickedElement.tagName?.toLowerCase();
    if (tagName === 'path' && clickedElement.id) {
      const room = rooms.find(r => r.id === clickedElement.id);

      if (room && room.type !== "structure") {
        event.stopPropagation();
        event.preventDefault();
        handleRoomClick(room.id);
        return;
      }
    }
    const elementsAtPoint = document.elementsFromPoint(event.clientX, event.clientY);

    for (const element of elementsAtPoint) {
      if (element.tagName?.toLowerCase() === 'path' && element.id) {
        const room = rooms.find(r => r.id === element.id);
        if (room && room.type !== "structure") {
          event.stopPropagation();
          event.preventDefault();
          handleRoomClick(room.id);
          return;
        }
      }
    }
  }, [rooms, handleRoomClick]);

  const handleRoomHover = useCallback((event) => {
    if (isMobile) return;
    
    const hoveredElement = event.target;
    
    if (hoveredElement.tagName === 'path' && hoveredElement.id) {
      const room = rooms.find(r => r.id === hoveredElement.id);
      if (room && room.type !== "structure") {
        const svgElement = svgRef.current?.querySelector('svg');
        if (svgElement) {
          const currentSvgPathIds = Array.from(svgElement.querySelectorAll('path')).map(p => p.id).filter(id => id);
          if (currentSvgPathIds.includes(hoveredElement.id)) {
            setHoveredRoomId(hoveredElement.id);
            manageSvgLayering(svgElement, hoveredElement.id, selectedRoom?.id);
            const pathElement = svgElement.querySelector(`#${hoveredElement.id}`);
            if (pathElement && !pathElement.classList.contains('highlighted')) {
              const labelsOverlay = svgElement.querySelector('#labels-overlay');
              if (labelsOverlay) {
                const labelElement = labelsOverlay.querySelector(`text[data-room-id="${hoveredElement.id}"]`);
                if (labelElement) {
                  labelElement.style.filter = 'brightness(0.6)';
                }
              }
            }
          }
        }
      }
    }
  }, [rooms, selectedRoom, manageSvgLayering, isMobile]);

  const handleRoomHoverEnd = useCallback((event) => {
    if (isMobile) return;
    
    const hoveredElement = event.target;
    
    if (hoveredElement.tagName === 'path' && hoveredElement.id) {
      const room = rooms.find(r => r.id === hoveredElement.id);
      if (room && room.type !== "structure") {
        const svgElement = svgRef.current?.querySelector('svg');
        if (svgElement) {
          const currentSvgPathIds = Array.from(svgElement.querySelectorAll('path')).map(p => p.id).filter(id => id);
          if (currentSvgPathIds.includes(hoveredElement.id)) {
            setHoveredRoomId(null);
            manageSvgLayering(svgElement, null, selectedRoom?.id);
            const pathElement = svgElement.querySelector(`#${hoveredElement.id}`);
            if (pathElement && !pathElement.classList.contains('highlighted')) {
              const labelsOverlay = svgElement.querySelector('#labels-overlay');
              if (labelsOverlay) {
                const labelElement = labelsOverlay.querySelector(`text[data-room-id="${hoveredElement.id}"]`);
                if (labelElement) {
                  labelElement.style.filter = '';
                }
              }
            }
          }
        }
      }
    }
  }, [rooms, selectedRoom, manageSvgLayering, isMobile]);

  const handleRoomClose = useCallback(() => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
    if (selectedRoom && svgRef.current) {
      const svgElement = svgRef.current.querySelector('svg');
      if (svgElement) {
        unhighlightRoom(selectedRoom.id, svgElement, viewMode);
        setHoveredRoomId(null);
        manageSvgLayering(svgElement, null, null);
      }
    }
    setRoomPopupVisible(false);
    setTimeout(() => {
      setSelectedRoom(null);
      setRoomPopupVisible(false);
      setShowPreviousRoom(false);
      setIsTransitioning(false);
    }, 300);
  }, [selectedRoom, viewMode, unhighlightRoom, manageSvgLayering]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectedRoom && popupRef.current && !popupRef.current.contains(event.target)) {
        if (showPreviousRoom || isTransitioning) {
          return;
        }
        const clickedElement = event.target;
        const isRoomPath = clickedElement.tagName === 'path' && clickedElement.id && rooms.some(room => room.id === clickedElement.id);
        if (isRoomPath) {
          return;
        }
        const mapContainer = svgRef.current;
        if (mapContainer && (mapContainer.contains(event.target) || event.target.closest('.main-content'))) {
          handleRoomClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedRoom, showPreviousRoom, isTransitioning, rooms, handleRoomClose]);

  useEffect(() => {
    const currentSvgRef = svgRef.current;
    if (currentSvgRef && svgContent) {
      if (rooms.length > 0) {
        applySvgColors(currentSvgRef, viewMode, rooms);
        const svgElementForLabels = currentSvgRef.querySelector('svg');
        if (svgElementForLabels) {
          applyLabelColors(svgElementForLabels, viewMode, rooms, hoveredRoomId, selectedRoom?.id);
        }
        // CRITICAL: requestAnimationFrame ensures SVG DOM is ready
        const svgElement = currentSvgRef.querySelector('svg');
        if (svgElement) {
          svgElement.removeEventListener('click', handleSvgClick);
          svgElement.removeEventListener('mouseover', handleRoomHover);
          svgElement.removeEventListener('mouseout', handleRoomHoverEnd);
          requestAnimationFrame(() => {
            if (!svgElement.isConnected) return;
            svgElement.addEventListener('click', handleSvgClick);
            if (!isMobile) {
              svgElement.addEventListener('mouseover', handleRoomHover);
              svgElement.addEventListener('mouseout', handleRoomHoverEnd);
            }
          });
        }
        const svgElementForLayering = currentSvgRef.querySelector('svg');
        if (svgElementForLayering) {
          // CRITICAL FIX: Filter to current SVG paths only to avoid floor-switch issues
          const currentSvgPathIds = Array.from(svgElementForLayering.querySelectorAll('path')).map(p => p.id).filter(id => id);
          const validRooms = rooms.filter(room => currentSvgPathIds.includes(room.id));
          const roomElements = validRooms.map(room => {
            const pathElement = currentSvgRef.querySelector(`#${room.id}`);
            if (pathElement) {
              return { element: pathElement, room };
            }
            return null;
          }).filter(Boolean);
          roomElements.forEach(({ element, room }) => {
            // CRITICAL: Remove fill="none" - Edge blocks pointer events
            if (element.getAttribute('fill') === 'none') {
              element.removeAttribute('fill');
            }
            element.classList.remove('room-path', 'structure', 'interactive-room');
            element.classList.add('room-path');
            if (room.type === "structure") {
              element.classList.add('structure');
              element.style.cursor = "default";
              element.style.pointerEvents = "none";
            } else {
              element.classList.add('interactive-room');
              element.style.cursor = "pointer";
              element.style.pointerEvents = "all";
              element.style.strokeWidth = "";
              element.style.filter = "";
              element.style.transition = "";
            }
          });
          manageSvgLayering(svgElementForLayering, hoveredRoomId, selectedRoom?.id);
        }
      } else {
        makePathsVisible(currentSvgRef);
      }
    }
    return () => {
      if (currentSvgRef) {
        const svgElement = currentSvgRef.querySelector('svg');
        if (svgElement) {
          svgElement.removeEventListener('click', handleSvgClick);
          if (!isMobile) {
            svgElement.removeEventListener('mouseover', handleRoomHover);
            svgElement.removeEventListener('mouseout', handleRoomHoverEnd);
          }
        }
      }
    };
  }, [svgContent, rooms, viewMode, applySvgColors, applyLabelColors, makePathsVisible, handleSvgClick, handleRoomHover, handleRoomHoverEnd, manageSvgLayering, hoveredRoomId, selectedRoom, isTransitioning, isMobile]);

  const persistCurrentEdits = useCallback((roomId, nextForm) => {
    const roomType = nextForm.type;
    const payload = {
      type: nextForm.type
    };
    if (!['structure'].includes(roomType)) {
      payload.name = nextForm.name;
    }
    if (!['structure', 'service', 'bathroom', 'stairs', 'elevator'].includes(roomType)) {
      payload.status = nextForm.status;
      payload.capacity = Number(nextForm.capacity) || 0;
    }
    if (['laboratory', 'classroom', 'study'].includes(roomType)) {
      payload.default_status = nextForm.default_status;
    }
    if (nextForm.alias && nextForm.alias.trim() !== '') {
      payload.alias = nextForm.alias.trim();
    } else {
      payload.alias = '';
    }
    
    setEditedRooms(prev => ({ ...prev, [roomId]: payload }));
  }, []);

  const handleFieldChange = (field, value) => {
    let updated = { ...formData, [field]: value };
    if (field === 'type') {
      const newType = value;
      if (['structure'].includes(newType)) {
        updated.name = '';
      }
      if (['structure', 'service', 'bathroom', 'stairs', 'elevator'].includes(newType)) {
        updated.status = 'unknown';
        updated.capacity = 0;
      }
    }
    
    setFormData(updated);
    if (selectedRoom) {
      persistCurrentEdits(selectedRoom.id, updated);
    }
  };

  const getFieldDisabled = (field) => {
    const roomType = formData.type;
    
    switch (field) {
      case 'name':
        return roomType === 'structure';
      case 'status':
        return ['structure', 'service', 'bathroom', 'stairs', 'elevator'].includes(roomType);
      case 'capacity':
      return ['structure', 'service', 'bathroom', 'stairs', 'elevator'].includes(roomType);
    case 'default_status':
      return !['laboratory', 'classroom', 'study'].includes(roomType);
    case 'alias':
      return false;
      default:
        return false;
    }
  };

  const handleSaveAll = async () => {
    const entries = Object.entries(editedRooms);
    if (entries.length === 0) return;
    setSaving(true);
    try {
      const requests = entries.map(([roomId, payload]) => (
        fetch(`/api/admin/room-data/${currentFloor}/${roomId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).then(async (res) => ({ ok: res.ok, body: await res.json(), roomId }))
      ));
      const results = await Promise.allSettled(requests);
      const failed = results.filter(r => r.status === 'fulfilled' ? !r.value.ok : true);
      if (failed.length > 0) {
        setSnackbar({ open: true, message: `Saved with ${failed.length} error(s)`, severity: 'warning' });
      } else {
        setSnackbar({ open: true, message: `All changes saved (${entries.length})`, severity: 'success' });
      }
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getPopupStyle = () => {
    const baseStyle = {
      position: "fixed",
      minHeight: "100px",
      backgroundColor: 'rgba(42, 42, 42, 0.5)',
      border: '1px solid rgba(74, 74, 74, 0.4)',
      backdropFilter: 'blur(12px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      color: '#ffffff'
    };
    if (isMobile) {
      return {
        ...baseStyle,
        bottom: 70,
        left: 0,
        right: 0,
        width: "100%",
        maxWidth: "100%",
        borderRadius: "16px 16px 0 0",
        marginBottom: 0,
        p: 1.5
      };
    } else {
      return {
        ...baseStyle,
        bottom: 0,
        left: "calc(50% - 275px)",
        maxWidth: "550px",
        width: "550px",
        borderRadius: "8px 8px 0 0",
        p: 2,
        zIndex: 1001,
        margin: "0 auto"
      };
    }
  };

  const renderLoadingState = () => (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      height="60vh"
      minHeight="400px"
    >
      <CircularProgress />
    </Box>
  );


  if (error) {
    return (
      <div className="main-content" style={{ 
        paddingTop: isMobile ? '72px' : '80px',
        paddingBottom: isMobile ? '80px' : '0',
        position: 'relative',
        textAlign: 'left',
        background: 'linear-gradient(180deg, #1a1a1a 0%, #0f0f0f 100%)',
        minHeight: '100vh'
      }}>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          height={isMobile 
            ? "calc(100vh - 72px - 80px - 40px)" 
            : "calc(100vh - 80px)"
          }
          flexDirection="column"
        >
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Map Data
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffffff' }}>
            {error}
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className="main-content" style={{ 
      paddingTop: isMobile ? '72px' : '80px',
      paddingBottom: isMobile ? '60px' : '0',
      position: 'relative',
      textAlign: 'left',
      background: 'linear-gradient(180deg, #1a1a1a 0%, #0f0f0f 100%)',
      minHeight: '100vh'
    }}>
      <Box sx={{ 
        minHeight: isMobile 
          ? "calc(100vh - 72px - 60px - 40px)" 
          : "calc(100vh - 80px)", 
        position: "relative"
      }}>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          px: 2
        }}>
          <Paper sx={{ 
            p: 1, 
            borderRadius: 2, 
            backgroundColor: 'rgba(42, 42, 42, 0.75)', 
            border: '1px solid rgba(74, 74, 74, 0.6)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <ToggleButtonGroup
              value={currentFloor}
              exclusive
              onChange={handleFloorChange}
              orientation="horizontal"
              size="small"
              disabled={!isLoaded}
              sx={{ 
                '& .MuiToggleButton-root': {
                  fontWeight: 'bold',
                  minWidth: '50px',
                  height: '32px',
                  color: '#ffffff',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(74, 74, 74, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      borderColor: 'primary.dark'
                    }
                  },
                  '&.Mui-disabled': {
                    color: '#ffffff !important',
                    fontWeight: 'bold !important',
                    opacity: '0.6'
                  }
                }
              }}
            >
              <ToggleButton value={"F3"}>F3</ToggleButton>
              <ToggleButton value={"F1"}>F1</ToggleButton>
              <ToggleButton value={"S1"}>S1</ToggleButton>
            </ToggleButtonGroup>
          </Paper>
          <Paper sx={{ 
            p: 1, 
            borderRadius: 2, 
            backgroundColor: 'rgba(42, 42, 42, 0.75)', 
            border: '1px solid rgba(74, 74, 74, 0.6)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{ 
                '& .MuiToggleButton-root': {
                  color: '#ffffff',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(74, 74, 74, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      borderColor: 'primary.dark'
                    }
                  }
                }
              }}
            >
              <ToggleButton value={"type"}>
                <CategoryIcon sx={{ mr: 1, fontSize: 16 }} />
                Type
              </ToggleButton>
              <ToggleButton value={"occupancy"}>
                <PeopleIcon sx={{ mr: 1, fontSize: 16 }} />
                Occupancy
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveAll}
            disabled={saving || Object.keys(editedRooms).length === 0}
            sx={{
              backgroundColor: '#AA3333',
              color: '#ffffff',
              borderRadius: 2,
              px: 2,
              py: 1,
              fontWeight: 'bold',
              height: '40px',
              '&:hover': {
                backgroundColor: '#8B2A2A'
              },
              '&:disabled': {
                backgroundColor: 'rgba(170, 51, 51, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            {saving ? 'Saving…' : `Save Changes${Object.keys(editedRooms).length ? ` (${Object.keys(editedRooms).length})` : ''}`}
          </Button>
        </Box>

        <Box
          sx={{
            minHeight: "100%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: isMobile ? "center" : "flex-start",
            paddingTop: isMobile ? "20px" : "10px",
            paddingBottom: "20px",
            paddingLeft: "20px",
            paddingRight: "20px",
            boxSizing: "border-box"
          }}
        >
          {!isLoaded ? (
            renderLoadingState()
          ) : svgContent ? (
            <Box
              ref={svgRef}
              sx={{
                maxWidth: "100%",
                transform: "scale(1)",
                transformOrigin: "top center",
                transition: "all 0.3s ease-in-out, opacity 0.2s ease-in-out",
                opacity: isLoaded ? 1 : 0,
                "& svg": {
                  height: "auto",
                  width: "100%",
                  maxWidth: "100%",
                  border: "none",
                  "& #icons-overlay": {
                    pointerEvents: "none !important",
                    cursor: "default !important"
                  },
                  "& #icons-overlay path": {
                    fillOpacity: "0.8 !important",
                    strokeOpacity: "0 !important"
                  },
                  "& #labels-overlay": {
                    pointerEvents: "none !important",
                    cursor: "default !important"
                  },
                  "& #labels-overlay text": {
                    pointerEvents: "none !important",
                    userSelect: "none"
                  }
                },
                 "& path": {
                   stroke: "#8a8a8a",
                   strokeWidth: "1",
                   strokeOpacity: "1",
                   opacity: "1",
                   cursor: "pointer",
                   pointerEvents: "all !important",
                   WebkitTapHighlightColor: "rgba(0, 0, 0, 0)",
                   WebkitUserSelect: "none",
                   WebkitTouchCallout: "none",
                   transition: "stroke-width 0.1s cubic-bezier(0.4, 0, 0.2, 1), filter 0.1s cubic-bezier(0.4, 0, 0.2, 1)"
                 },
                 "& .interactive-room": {
                   pointerEvents: "all !important",
                   cursor: "pointer !important"
                 },
                 "& .structure": {
                   pointerEvents: "none !important",
                   cursor: "default !important"
                 }
              }}
              dangerouslySetInnerHTML={{ __html: svgContent }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "60vh",
                width: "100%",
                background: "linear-gradient(180deg, #1a1a1a 0%, #0f0f0f 100%)",
                border: "2px dashed #4a4a4a",
                borderRadius: 2,
                p: 4
              }}
            >
              <Typography variant="h6" sx={{ color: '#ffffff' }} gutterBottom>
                Floor Plan Not Available
              </Typography>
              <Typography variant="body2" sx={{ color: '#ffffff' }} align="center">
                The floor plan for {currentFloor} is not yet available.<br />
                Please check back later or contact support.
              </Typography>
            </Box>
          )}
        </Box>

        {selectedRoom && (
          <Slide 
            direction="up" 
            in={roomPopupVisible} 
            timeout={300}
            mountOnEnter 
            unmountOnExit
          >
            <Paper 
              key={`current-room-${selectedRoom.id}`}
              ref={popupRef}
              sx={{ 
                ...getPopupStyle(),
                zIndex: 1001
              }}
            >
              <Box sx={{ position: "relative", mb: isMobile ? 1 : 1 }}>
                {isMobile ? (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', textAlign: 'center', width: '100%' }}>
                      {selectedRoom.name || selectedRoom.id}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        setRoomPopupVisible(false);
                        openEditorForRoom(selectedRoom);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        backgroundColor: 'rgba(74, 74, 74, 0.8)',
                        border: '1px solid rgba(74, 74, 74, 0.8)',
                        color: '#ffffff',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        '&:hover': {
                          backgroundColor: 'rgba(170, 51, 51, 0.8)',
                          borderColor: 'rgba(170, 51, 51, 0.8)'
                        }
                      }}
                      size="small"
                    >
                      ✏️
                    </IconButton>
                    <IconButton
                      onClick={handleRoomClose}
                      sx={{
                        width: 28,
                        height: 28,
                        backgroundColor: 'rgba(74, 74, 74, 0.8)',
                        border: '1px solid rgba(74, 74, 74, 0.8)',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.8)',
                          borderColor: 'rgba(244, 67, 54, 0.8)'
                        },
                        lineHeight: 1,
                        paddingTop: '4px'
                      }}
                      size="small"
                    >
                      ×
                    </IconButton>
                  </>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Typography variant="h6" gutterBottom>
                      {selectedRoom.name || selectedRoom.id}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        onClick={() => {
                          setRoomPopupVisible(false);
                          openEditorForRoom(selectedRoom);
                        }}
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: 'rgba(74, 74, 74, 0.8)',
                          border: '1px solid rgba(74, 74, 74, 0.8)',
                          color: '#ffffff',
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          '&:hover': {
                            backgroundColor: 'rgba(170, 51, 51, 0.8)',
                            borderColor: 'rgba(170, 51, 51, 0.8)'
                          }
                        }}
                        size="small"
                      >
                        ✏️
                      </IconButton>
                      <IconButton
                        onClick={handleRoomClose}
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: 'rgba(74, 74, 74, 0.8)',
                          border: '1px solid rgba(74, 74, 74, 0.8)',
                          color: '#ffffff',
                          fontSize: '1.4rem',
                          fontWeight: 'bold',
                          '&:hover': { 
                            backgroundColor: 'rgba(244, 67, 54, 0.8)',
                            borderColor: 'rgba(244, 67, 54, 0.8)',
                            color: '#ffffff'
                          }
                        }}
                        size="small"
                      >
                        ×
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#ffffff', minWidth: 'fit-content' }}>
                    Type:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {selectedRoom.type || 'Unknown'}
                  </Typography>
                </Box>
                
                {hasValidCapacity(selectedRoom) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#ffffff', minWidth: 'fit-content' }}>
                      Capacity:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {selectedRoom.capacity} people
                    </Typography>
                  </Box>
                )}
                
                {hasValidStatus(selectedRoom) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#ffffff', minWidth: 'fit-content' }}>
                      Status:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {selectedRoom.status}
                    </Typography>
                  </Box>
                )}
              </Box>

            </Paper>
          </Slide>
        )}

        <Dialog 
          open={editorOpen} 
          onClose={closeEditor} 
          maxWidth="sm" 
          fullWidth
          onClick={(e) => e.stopPropagation()}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(42, 42, 42, 0.5)',
              border: '1px solid rgba(74, 74, 74, 0.4)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              color: '#ffffff'
            }
          }}
        >
          <DialogTitle sx={{ color: '#ffffff' }}>Edit Room {selectedRoom?.id}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField 
                  fullWidth 
                  label="Name" 
                  value={formData.name || ''} 
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  disabled={getFieldDisabled('name')}
                  helperText={getFieldDisabled('name') ? 'Name not applicable for structure rooms' : ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(74, 74, 74, 0.6)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#AA3333' }
                    },
                    '& .MuiInputLabel-root': { color: '#e0e0e0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#AA3333' },
                    '& .MuiFormHelperText-root': { color: '#e0e0e0' }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  fullWidth 
                  label="Alias" 
                  value={formData.alias || ''} 
                  onChange={(e) => handleFieldChange('alias', e.target.value)}
                  helperText="Room alias for schedule matching (e.g., 'Room 101', 'Class 4'). This connects the room to schedule data."
                  placeholder="Enter room alias for schedule integration"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(74, 74, 74, 0.6)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#AA3333' }
                    },
                    '& .MuiInputLabel-root': { color: '#e0e0e0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#AA3333' },
                    '& .MuiFormHelperText-root': { color: '#e0e0e0' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  select 
                  fullWidth 
                  label="Type" 
                  value={formData.type || ''} 
                  onChange={(e) => handleFieldChange('type', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(74, 74, 74, 0.6)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#AA3333' },
                      '& .MuiSelect-icon': { color: '#ffffff' }
                    },
                    '& .MuiInputLabel-root': { color: '#e0e0e0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#AA3333' }
                  }}
                >
                  {['laboratory','classroom','office','study','service','bathroom','stairs','elevator','structure','other'].map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  select 
                  fullWidth 
                  label="Status" 
                  value={formData.status || ''} 
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  disabled={getFieldDisabled('status')}
                  helperText={getFieldDisabled('status') ? 'Status not applicable for this room type' : ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(74, 74, 74, 0.6)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#AA3333' },
                      '& .MuiSelect-icon': { color: '#ffffff' }
                    },
                    '& .MuiInputLabel-root': { color: '#e0e0e0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#AA3333' },
                    '& .MuiFormHelperText-root': { color: '#e0e0e0' }
                  }}
                >
                  {['available','occupied','unavailable','unknown'].map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  type="number" 
                  label="Capacity" 
                  value={formData.capacity ?? 0} 
                  onChange={(e) => handleFieldChange('capacity', e.target.value)}
                  disabled={getFieldDisabled('capacity')}
                  helperText={getFieldDisabled('capacity') ? 'Capacity not applicable for this room type' : ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(74, 74, 74, 0.6)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#AA3333' }
                    },
                    '& .MuiInputLabel-root': { color: '#e0e0e0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#AA3333' },
                    '& .MuiFormHelperText-root': { color: '#e0e0e0' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select 
                  fullWidth 
                  label="Default Status" 
                  value={formData.default_status || 'available'} 
                  onChange={(e) => handleFieldChange('default_status', e.target.value)}
                  disabled={getFieldDisabled('default_status')}
                  helperText={getFieldDisabled('default_status') ? 'Default status only available for laboratory, classroom, and study rooms' : 'Default status when no classes are scheduled (unavailable = orange)'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(74, 74, 74, 0.6)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#AA3333' },
                      '& .MuiSelect-icon': { color: '#ffffff' }
                    },
                    '& .MuiInputLabel-root': { color: '#e0e0e0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#AA3333' },
                    '& .MuiFormHelperText-root': { color: '#e0e0e0' }
                  }}
                >
                  {['available','unavailable'].map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions onClick={(e) => e.stopPropagation()} sx={{ backgroundColor: 'rgba(42, 42, 42, 0.3)' }}>
            <Button 
              onClick={closeEditor}
              sx={{
                backgroundColor: '#AA3333',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#8B2A2A'
                }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default AdminRoomsTab;


